/**
 * Shared entity URL resolution — used by CommandPalette and SearchPage.
 *
 * Centralised here to avoid duplication and make the URL mapping testable.
 */

export interface EntitySearchResult {
  entity_type: string;
  entity_id: string;
  name: string;
  metadata?: Record<string, string>;
}

/**
 * Returns the client-side route URL for a given entity search result.
 *
 * Special cases:
 * - `meeting_instance` needs `metadata.series_id` for the nested route.
 *   Falls back to `/meetings` when missing.
 * - `tag` and `quick_capture` navigate to their list pages (no detail route).
 */
export function getEntityUrl(result: EntitySearchResult): string {
  switch (result.entity_type) {
    case 'task': return `/tasks/${result.entity_id}`;
    case 'meeting_instance': {
      const seriesId = result.metadata?.series_id;
      return seriesId
        ? `/meetings/${seriesId}/${result.entity_id}`
        : `/meetings/standalone/${result.entity_id}`;
    }
    case 'meeting_series': return `/meetings/${result.entity_id}`;
    case 'person': return `/people/${result.entity_id}`;
    case 'tag': return '/tags';
    case 'scope': return `/projects/${result.entity_id}`;
    case 'reference_doc': return `/views/${result.entity_id}`;
    case 'quick_capture': return '/inbox';
    case 'note': return `/notes/${result.entity_id}`;
    case 'attachment': return `/api/attachments/${result.entity_id}/download`;
    default: return '/';
  }
}

const RECENT_SEARCHES_KEY = 'dossier:recentSearches';
const MAX_RECENT = 5;

export function getRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveRecentSearch(query: string) {
  const trimmed = query.trim();
  if (!trimmed) return;
  const recent = getRecentSearches().filter(s => s !== trimmed);
  recent.unshift(trimmed);
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}
